import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { OPS_POINT_MAX, OPS_POINT_MIN } from '@cortex/ops-core';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { Select, type SelectOption } from '@cortex/web-design-system/components/ui/Select';
import { opsWorkMinutesOptions } from './OpsPrimitives.js';
import { useSetOpsRequestPoint, useSetOpsRequestWorkMinutes } from '../../../hooks/useOps.js';
import type { OpsRequest } from '../../../types/ops.js';

/** 工数ポイントカードの props。 */
export interface OpsPointCardProps {
  /** 表示対象の依頼（point / pointSource を参照）。Firestore 発番かつ closed（解決済み）の依頼のみ渡す。 */
  request: OpsRequest;
}

/**
 * 工数ポイント（0–21、TL 確定値。0=対応不要）の表示・編集カード。工数ポイントは解決後に確定するため、呼び出し側で
 * Firestore 発番かつ closed（解決済み）の依頼にのみ描画する（open / investigating / 差戻し中は非表示）。見積もり待ち + 0–21 の
 * Select で設定する（変更即保存。ステート / 緊急度の Select と同じ挙動）。設定元の provenance（pointSource=manual=人手確定 /
 * ai=AI 見積もり自動採用）を補足表示する。legacy（シート由来）依頼はポイントがシート / BQ 側にあり本カードでは扱わないため、呼び出し側で描画しない。
 * Backlog への反映は backend の reconciler が行う（ここでは Firestore の point を更新するだけ）。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 依頼詳細（Firestore 発番・closed）の工数ポイント / 作業時間の表示・編集カード。どちらも解決後に確定するため closed のみ表示。ポイントは見積もり待ち + 0–21（0=対応不要）の Select で確定し、設定元（manual / ai）を provenance として補足表示する。作業時間（分）は未記録を既定とする Select で任意に記録し、種別ごとの実績中央値を目安としてラベルに示す（自動選択はしない）
 * @graph-connects useSetOpsRequestPoint [calls] 工数ポイント設定（PATCH）の実行
 * @graph-connects useSetOpsRequestWorkMinutes [calls] 作業時間設定（PATCH）の実行
 * @graph-connects opsWorkMinutesOptions [calls] 作業時間ドロップダウンの選択肢を組み立てる
 */
export function OpsPointCard({ request }: OpsPointCardProps): ReactNode {
  const { t } = useTranslation();
  const setPointMutation = useSetOpsRequestPoint();
  const setWorkMinutesMutation = useSetOpsRequestWorkMinutes();
  const { point, pointSource, workMinutes } = request;

  const options: SelectOption[] = [
    { value: '', label: t('ops.detail.point.pending') },
    ...Array.from({ length: OPS_POINT_MAX - OPS_POINT_MIN + 1 }, (_unused, i) => {
      const n = OPS_POINT_MIN + i;
      // 0 は「対応不要（工数 0）」を意味する。数値だけでは伝わらないのでラベルで明示する。
      return { value: String(n), label: n === 0 ? t('ops.detail.point.noWork') : String(n) };
    }),
  ];

  const submit = (raw: string): void => {
    const next = raw === '' ? null : Number(raw);
    if (next === point) return;
    setPointMutation.mutate({ id: request.id, point: next });
  };

  // 選択肢は共有ヘルパから。API は選択肢外の正の整数も許すので、保存済みの値が一覧に無ければ
  // その値を選択肢として足す（足さないと実測 45 分の依頼が「未記録」に見え、上書きされる）。
  const baseOptions = opsWorkMinutesOptions(request.category, t);
  const workMinutesOptions: SelectOption[] =
    workMinutes != null && !baseOptions.some((o) => o.value === String(workMinutes))
      ? [
          baseOptions[0],
          {
            value: String(workMinutes),
            label: t('ops.detail.workMinutes.option', { count: workMinutes }),
          },
          ...baseOptions.slice(1),
        ]
      : baseOptions;

  const submitWorkMinutes = (raw: string): void => {
    const next = raw === '' ? null : Number(raw);
    if (next === (workMinutes ?? null)) return;
    setWorkMinutesMutation.mutate({ id: request.id, workMinutes: next });
  };

  return (
    <Card>
      <CardHeader title={t('ops.detail.point.title')} />
      <CardContent className="space-y-2">
        <Select
          aria-label={t('ops.detail.point.title')}
          options={options}
          value={point === null ? '' : String(point)}
          onChange={(e) => {
            submit(e.target.value);
          }}
          disabled={setPointMutation.isPending}
          fullWidth
        />

        {pointSource !== null && point !== null && (
          <p className="text-xs text-[var(--color-text-muted)]">
            {t(
              pointSource === 'ai' ? 'ops.detail.point.sourceAi' : 'ops.detail.point.sourceManual',
            )}
          </p>
        )}

        {setPointMutation.isError && (
          <span className="text-xs text-[var(--color-error)]">
            {t('ops.detail.point.saveFailed')}
          </span>
        )}

        {/* 作業時間はポイントと同じ「工数」の話なので、カードを増やさずこのカードに同居させる。 */}
        <div className="border-t border-[var(--color-border)] pt-2">
          <p className="mb-1 text-xs text-[var(--color-text-secondary)]">
            {t('ops.detail.workMinutes.title')}
          </p>
          <Select
            aria-label={t('ops.detail.workMinutes.title')}
            options={workMinutesOptions}
            value={workMinutes == null ? '' : String(workMinutes)}
            onChange={(e) => {
              submitWorkMinutes(e.target.value);
            }}
            disabled={setWorkMinutesMutation.isPending}
            fullWidth
          />
          {setWorkMinutesMutation.isError && (
            <span className="text-xs text-[var(--color-error)]">
              {t('ops.detail.workMinutes.saveFailed')}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
